import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { Box, TableCell, TableRow, Typography } from "@mui/material";

import { FormContainer } from "../../customer/customerManagement/styles";
import {
  ActionsIconButton,
  Heading1,
} from "../../../common/components/styled/styled";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import { ExpertDialog } from "./ExpertDialog";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
  updateExpertEnabledState,
} from "../../user/expertUsersSlice";
import { expertRoles } from "../../user/accessRightChecks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { fetchEezyCompanies } from "../../invoices/invoiceSlice";
import { EezyCompany } from "../../invoices/invoiceAPI";
import SearchField from "../../../common/components/functional/SearchField";
import { compareUsersByLastName } from "../../../common/utils/users";
import { ExpertActionsMenu, MenuItemKey } from "./ExpertActionsMenu";
import { ExpertOutOfOfficeDialog } from "./ExpertOutOfOfficeDialog";
import { ExpertUser } from "../../user/types/entities";
import {
  formatOutOfOfficePeriod,
  isOutOfOfficeActive,
} from "../../home/toolbarMenu/statusMessageMappers";

type MenuState = {
  anchorEl: HTMLButtonElement | null;
  user: ExpertUser | null;
};

export const ExpertsControlPanel = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const experts = useAppSelector((state) => selectAllExpertUsers(state));
  const { status } = useAppSelector((state) => state.expertUsers);
  const isLoading = status === "pending";

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );
  const [selectedExpertId, setSelectedExpertId] = useState<string | null>(null);
  const [statusExpertId, setStatusExpertId] = useState<string | null>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [filteredExperts, setFilteredExperts] = useState(experts);
  const [menuState, setMenuState] = useState<MenuState>({
    anchorEl: null,
    user: null,
  });

  const openDialog = (id: string | null) => {
    setSelectedExpertId(id);
    setIsAddDialogOpen(true);
  };

  const closeDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedExpertId(null);
  };

  const onMenuOpen = (anchorEl: HTMLButtonElement, user: ExpertUser) => {
    setMenuState({ anchorEl, user });
  };

  const onMenuClose = () =>
    setMenuState({ ...menuState, anchorEl: null, user: null });

  const rows = filteredExperts.sort(compareUsersByLastName).map((e) => {
    const isAway = isOutOfOfficeActive(e.account.outOfOffice);

    return (
      <TableRow
        key={e.id}
        sx={{
          opacity: e.account.enabled === false ? 0.5 : 1,
          ...(isAway
            ? {
                color: "error.main",
                "& td, & .MuiTypography-root": { color: "inherit" },
              }
            : {}),
        }}
      >
        <TableCell>
          {e.account.lastName} {e.account.firstName}
        </TableCell>
        <TableCell>
          {e.account.eezyCompanyIds?.map((id) => (
            <Typography key={id} sx={{ fontSize: 14 }}>
              {
                eezyCompanyOptions.find((company) => company.eezyCompanyId === id)
                  ?.name
              }
            </Typography>
          ))}
        </TableCell>
        <TableCell>{e.account.emailAddress}</TableCell>
        <TableCell>
          {e.account.authScopes.map((scope) => (
            <Typography key={scope} sx={{ fontSize: 14 }}>
              {t(
                `settings:${expertRoles.find((r) => scope === r.id)?.name || ""}`,
              )}
            </Typography>
          ))}
        </TableCell>
        <TableCell>
          {e.account.registered ? t("common:yes") : t("common:no")}
        </TableCell>
        <TableCell>
          {e.account.enabled == null || e.account.enabled === true
            ? t("common:yes")
            : t("common:no")}
        </TableCell>
        <TableCell>
          {formatOutOfOfficePeriod(e.account.outOfOffice) ?? "–"}
        </TableCell>
        <TableCell>
          <ActionsIconButton
            size="small"
            onClick={(ev) => onMenuOpen(ev.currentTarget, e)}
          >
            <MoreIcon />
          </ActionsIconButton>
        </TableCell>
      </TableRow>
    );
  });

  useEffect(() => {
    setFilteredExperts(
      experts.filter(
        (e) =>
          e.account.firstName
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          e.account.lastName.toLowerCase().includes(searchString.toLowerCase()),
      ),
    );
  }, [searchString, experts]);

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
    dispatch(fetchEezyCompanies({})).then(
      (action) => isFulfilled(action) && setEezyCompanyOptions(action.payload),
    );
  }, [dispatch]);

  const menuActions = {
    [MenuItemKey.EDIT]: (user: ExpertUser | null) => {
      openDialog(user ? user.id : null);
    },
    [MenuItemKey.EDIT_STATUS]: (user: ExpertUser | null) => {
      setStatusExpertId(user ? user.id : null);
    },
    [MenuItemKey.DISABLE]: (user: ExpertUser | null) => {
      if (user) {
        dispatch(
          updateExpertEnabledState({
            expertId: user.id,
            version: user.account.version,
            enabled: false,
          }),
        );
      }
    },
    [MenuItemKey.ENABLE]: (user: ExpertUser | null) => {
      if (user) {
        dispatch(
          updateExpertEnabledState({
            expertId: user.id,
            version: user.account.version,
            enabled: true,
          }),
        );
      }
    },
  };

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <FormContainer>
        <Box display="flex" alignItems="center">
          <Heading1>{t("settings:experts")}</Heading1>
          <AddButton
            sx={{
              marginLeft: "1rem",
              height: "40px",
              borderRadius: "20px",
            }}
            variant="contained"
            label={t("common:add")}
            onClick={() => setIsAddDialogOpen(true)}
          />
        </Box>
        <Box width="600px">
          <SearchField
            label={t("search") || ""}
            value={searchString}
            onChange={(value) => setSearchString(value)}
            sx={{ marginBottom: 2 }}
          />
        </Box>
        <StripedTable
          columnHeaders={[
            t("common:name"),
            t("settings:company"),
            t("common:email"),
            t("settings:role"),
            t("settings:registered"),
            t("settings:enabled"),
            t("settings:away"),
            "",
          ]}
          itemRows={rows}
          disableRowClick={true}
        />
      </FormContainer>
      <ExpertDialog
        isOpen={isAddDialogOpen}
        eezyCompanyOptions={eezyCompanyOptions}
        expertId={selectedExpertId || ""}
        close={closeDialog}
      />
      <ExpertOutOfOfficeDialog
        expertId={statusExpertId}
        isOpen={!!statusExpertId}
        close={() => setStatusExpertId(null)}
      />
      <ExpertActionsMenu
        open={!!menuState.anchorEl}
        anchorEl={menuState.anchorEl}
        onClose={onMenuClose}
        user={menuState.user}
        onExited={onMenuClose}
        menuActions={menuActions}
      />
    </>
  );
};
